/**
 * Typed wrappers for every `/partner/*` and `/l/:token` route the portal
 * calls. One function per endpoint so a screen never builds a path by hand.
 */
import type { ApiClient } from './client';
import type {
  NoticeMode,
  NoticePrefs,
  NoticeRecipient,
  Account,
  AgentSetupBundle,
  AgentToken,
  AgentTokenCreateBody,
  AgentTokenCreated,
  OAuthApproveBody,
  OAuthRequestDetails,
  AuditEntry,
  BriefActionResponse,
  BriefResponse,
  CodeResponse,
  EmailChanged,
  EmailReverted,
  EmailRevertPreview,
  GoogleLinked,
  GoogleLinkReverted,
  GoogleLinkRevertPreview,
  StepUpVerified,
  Delivery,
  DeliveryStatus,
  Destination,
  DestinationCreated,
  DestinationKind,
  ExportJob,
  Funnel,
  HostedPage,
  HostedPageCreateBody,
  HostedPageList,
  HostedPagePatch,
  InstallHealth,
  LeadDetail,
  LeadSummary,
  FallbackRequest,
  Member,
  NetworkFallbackState,
  OpsFallbackRequest,
  OpsWidgetLimitRequest,
  WidgetLimitValues,
  WidgetUsageLimits,
  OpsNetworkFallback,
  Org,
  OrgUpdated,
  Origin,
  OriginPolicy,
  Page,
  PartnerRole,
  PendingChange,
  AgentChangeLinkPreview,
  PendingChangeApproved,
  PartnerType,
  PublishResponse,
  RoutingPatch,
  RoutingResponse,
  RoutingRule,
  RoutingRuleInput,
  SelectResponse,
  ShareLink,
  ShareLinkCreateBody,
  ShareLinkCreated,
  SharedDashboard,
  SetupProgress,
  Site,
  TermsAcceptance,
  TermsStatus,
  SiteCreated,
  SitePaused,
  SiteResumed,
  SiteSummary,
  CloseRequestState,
  CloseRequested,
  TimelineEntry,
  UnlistedAttempt,
  CspCheck,
  Verification,
  VerifyResponse,
  VerifyStarted,
  Vendor,
  VendorBulkResult,
  VendorImportResult,
  VendorInput,
  VendorConsentEvent,
  VendorReconsentInput,
  Whoami,
  ClaimInfo,
  ClaimResult,
  SignupReportPreview,
  SignupReportResult,
  MemberInvite,
  MemberInvitePreview,
  MemberInviteAccepted,
  OpsCounts,
  OpsPartner,
  OpsPartnerDetail,
  OpsPartnerState,
  PartnerLifecycle,
} from './types';
import type { ConfigGroup, StoredConfigResponse, WidgetConfigModel } from '../config/model';

const enc = encodeURIComponent;

export function partnerApi(client: ApiClient) {
  return {
    // Auth
    requestCode: (email: string) => client.post<CodeResponse>('/partner/auth/code', { email }, { auth: false }),
    verifyCode: (challenge_id: string, code: string) =>
      client.post<VerifyResponse>('/partner/auth/verify', { challenge_id, code }, { auth: false }),
    googleSignIn: (credential: string) =>
      client.post<VerifyResponse>('/partner/auth/google', { credential }, { auth: false }),
    selectOrg: (partner_id: string) => client.post<SelectResponse>('/partner/auth/select', { partner_id }),
    logout: (refresh_token: string) => client.post<void>('/partner/auth/logout', { refresh_token }, { auth: false }),
    whoami: () => client.get<Whoami>('/partner/auth/whoami'),

    // Your account (the person, not the organisation)
    getAccount: () => client.get<Account>('/partner/auth/account'),
    startEmailChange: (new_email: string) => client.post<CodeResponse>('/partner/auth/email-change/start', { new_email }),
    verifyEmailChange: (challenge_id: string, code: string) =>
      client.post<EmailChanged>('/partner/auth/email-change/verify', { challenge_id, code }),
    unlinkGoogle: () => client.delete<{ google_linked: false }>('/partner/auth/google'),
    // Migration 093: which AI agent notices this person gets.
    getNoticePrefs: () => client.get<NoticePrefs>('/partner/auth/account/notifications'),
    setNoticePrefs: (mode: NoticeMode) => client.put<{ mode: NoticeMode }>('/partner/auth/account/notifications', { mode }),
    // Migration 093: extra addresses for AI agent and security notices (Organisation).
    listNoticeRecipients: () => client.get<{ items: NoticeRecipient[]; max: number }>('/partner/org/notice-recipients'),
    addNoticeRecipient: (email: string) => client.post<NoticeRecipient>('/partner/org/notice-recipients', { email }),
    resendNoticeRecipient: (id: string) => client.post<NoticeRecipient>(`/partner/org/notice-recipients/${id}/resend`),
    removeNoticeRecipient: (id: string) => client.delete<void>(`/partner/org/notice-recipients/${id}`),
    // Step-up (migration 086): a code to the CURRENT email before the email change or a Google unlink.
    startStepUp: () => client.post<CodeResponse>('/partner/auth/step-up/start', {}),
    verifyStepUp: (challenge_id: string, code: string) =>
      client.post<StepUpVerified>('/partner/auth/step-up/verify', { challenge_id, code }),
    // The "This wasn't me" link from the email-changed notice: no session, the token is the authority.
    previewEmailRevert: (token: string) =>
      client.post<EmailRevertPreview>('/partner/auth/email-revert/preview', { token }, { auth: false }),
    revertEmail: (token: string) => client.post<EmailReverted>('/partner/auth/email-revert', { token }, { auth: false }),
    // Migration 090: link a Google account while signed in (needs the step-up), and its "This wasn't me" link.
    linkGoogle: (credential: string) => client.post<GoogleLinked>('/partner/auth/google/link', { credential }),
    previewGoogleLinkRevert: (token: string) =>
      client.post<GoogleLinkRevertPreview>('/partner/auth/google-link-revert/preview', { token }, { auth: false }),
    revertGoogleLink: (token: string) =>
      client.post<GoogleLinkReverted>('/partner/auth/google-link-revert', { token }, { auth: false }),

    // Org + members
    /** `terms` is the Partner Terms version the OrgSetup checkbox showed (required). */
    createOrg: (display_name: string, partner_type: PartnerType, terms: TermsAcceptance) =>
      client.post<Org>('/partner/org', { display_name, partner_type, ...terms }),
    getOrg: () => client.get<Org>('/partner/org'),
    getTerms: () => client.get<TermsStatus>('/partner/terms'),
    // An account an AI agent signed up (migration 080): its claim state, and
    // the named person's confirmation.
    // Owner asks FinderlyFix to close the organisation and delete its data (staff act on it).
    getCloseRequest: () => client.get<CloseRequestState>('/partner/org/close-request'),
    requestClose: (confirm_name: string, reason?: string) =>
      client.post<CloseRequested>('/partner/org/close-request', reason ? { confirm_name, reason } : { confirm_name }),
    getClaim: () => client.get<ClaimInfo>('/partner/org/claim'),
    claimOrg: (terms_version: string) => client.post<ClaimResult>('/partner/org/claim', { terms_version }),
    // The public "this wasn't us" link (no sign-in; the token is the link's).
    previewSignupReport: (token: string) =>
      client.post<SignupReportPreview>('/partner/signup/report/preview', { token }, { auth: false }),
    reportSignup: (token: string) => client.post<SignupReportResult>('/partner/signup/report', { token }, { auth: false }),
    acceptTerms: (terms: TermsAcceptance) => client.post<TermsStatus>('/partner/terms/accept', terms),
    updateOrg: (patch: Partial<Pick<Org, 'display_name' | 'partner_type' | 'media_retention_days'>>) =>
      client.patch<OrgUpdated>('/partner/org', patch),
    listMembers: (cursor?: string) => client.get<Page<Member>>('/partner/org/members', { query: { cursor } }),
    inviteMember: (email: string, role: PartnerRole) =>
      client.post<{ user_id: number; role: PartnerRole; invited: true }>('/partner/org/members/invite', { email, role }),
    updateMember: (userId: number, role: PartnerRole) =>
      client.patch<{ user_id: number; role: PartnerRole }>(`/partner/org/members/${userId}`, { role }),
    removeMember: (userId: number) => client.delete<void>(`/partner/org/members/${userId}`),
    // Migration 083: invitations an AI agent asked for. Owners and admins list
    // and revoke them; the invitee previews and accepts from the emailed link
    // (no sign-in: the token is the link's).
    listMemberInvites: () => client.get<{ items: MemberInvite[] }>('/partner/org/invites'),
    revokeMemberInvite: (id: string) => client.delete<void>(`/partner/org/invites/${id}`),
    previewMemberInvite: (token: string) => client.post<MemberInvitePreview>('/partner/invites/preview', { token }, { auth: false }),
    acceptMemberInvite: (token: string) => client.post<MemberInviteAccepted>('/partner/invites/accept', { token }, { auth: false }),

    // Sites
    listSites: (cursor?: string) => client.get<Page<SiteSummary>>('/partner/sites', { query: { cursor, limit: 200 } }),
    createSite: (label: string) => client.post<SiteCreated>('/partner/sites', { label }),
    getSite: (key: string) => client.get<Site>(`/partner/sites/${enc(key)}`),
    rotateSite: (key: string) =>
      client.post<{ new_site_key: string; grace_until: string }>(`/partner/sites/${enc(key)}/rotate`, {}),
    listOrigins: (key: string) => client.get<{ items: Origin[] }>(`/partner/sites/${enc(key)}/origins`),
    addOrigin: (key: string, origin: string) => client.post<Origin>(`/partner/sites/${enc(key)}/origins`, { origin }),
    removeOrigin: (key: string, id: number) => client.delete<void>(`/partner/sites/${enc(key)}/origins/${id}`),
    renameSite: (key: string, label: string) => client.patch<Site>(`/partner/sites/${enc(key)}`, { label }),
    /** Stops the widget (and hosted pages) on the live key and its test twin. Reversible. */
    pauseSite: (key: string) => client.post<SitePaused>(`/partner/sites/${enc(key)}/pause`, {}),
    resumeSite: (key: string) => client.post<SiteResumed>(`/partner/sites/${enc(key)}/resume`, {}),

    // Network fallback (migration 082)
    getNetworkFallback: (key: string) => client.get<NetworkFallbackState>(`/partner/sites/${enc(key)}/network-fallback`),
    /** Turning it on sends `acknowledged_disclosure` — only from the confirm step. */
    setNetworkFallback: (key: string, enabled: boolean) =>
      client.put<NetworkFallbackState>(`/partner/sites/${enc(key)}/network-fallback`, enabled ? { enabled, acknowledged_disclosure: true } : { enabled }),
    requestNetworkFallback: (key: string, note?: string) =>
      client.post<NetworkFallbackState>(`/partner/sites/${enc(key)}/network-fallback/request`, note ? { note } : {}),
    cancelNetworkFallbackRequest: (key: string) => client.delete<void>(`/partner/sites/${enc(key)}/network-fallback/request`),
    notifyNeutralNetwork: () => client.post<FallbackRequest>('/partner/network-fallback/notify', {}),
    widgetUsageLimits: (key: string) => client.get<WidgetUsageLimits>(`/partner/sites/${enc(key)}/usage-limits`),
    requestWidgetLimits: (key: string, limits: WidgetLimitValues, note: string) =>
      client.post<WidgetUsageLimits>(`/partner/sites/${enc(key)}/usage-limits/requests`, {
        ...limits,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    cancelWidgetLimitRequest: (key: string) => client.delete<void>(`/partner/sites/${enc(key)}/usage-limits/requests`),
    cancelNeutralNetworkNotify: () => client.delete<void>('/partner/network-fallback/notify'),
    setOriginPolicy: (key: string, origin_policy: OriginPolicy) =>
      client.patch<Site>(`/partner/sites/${enc(key)}`, { origin_policy }),
    setOriginBlocked: (key: string, id: number, blocked: boolean) =>
      client.patch<Origin>(`/partner/sites/${enc(key)}/origins/${id}`, { blocked }),
    unlistedAttempts: (key: string) =>
      client.get<{ items: UnlistedAttempt[] }>(`/partner/sites/${enc(key)}/unlisted-attempts`),
    verify: (key: string, url: string, tier: 1 | 2 | 3) =>
      client.post<VerifyStarted>(`/partner/sites/${enc(key)}/verify`, { url, tier }),
    /** The facts the setup rail derives "done" from (setup/status.ts). */
    setupProgress: (site_key?: string) => client.get<SetupProgress>('/partner/setup-progress', { query: { site_key } }),
    listVerifications: (key: string) => client.get<Page<Verification>>(`/partner/sites/${enc(key)}/verify`),
    checkCsp: (key: string, url: string, required: Array<{ directive: string; sources: string[] }>) =>
      client.post<CspCheck>(`/partner/sites/${enc(key)}/csp-check`, { url, required }),

    // Config
    getConfig: (key: string, state: 'draft' | 'published') =>
      client.get<StoredConfigResponse>(`/partner/sites/${enc(key)}/config`, { query: { state } }),
    saveDraft: (key: string, patch: Partial<Record<ConfigGroup, unknown>>) =>
      client.put<{ version: number; config: WidgetConfigModel; published?: boolean; unchanged?: boolean }>(`/partner/sites/${enc(key)}/config/draft`, patch),
    publish: (key: string, version: number) =>
      client.post<PublishResponse>(`/partner/sites/${enc(key)}/config/publish`, { version }),
    /** Back to what is live: the live document is republished as a new version (no history is lost). */
    discardDraft: (key: string) =>
      client.post<{ discarded: boolean; version: number; published: true; discarded_version?: number; restored_from?: number }>(
        `/partner/sites/${enc(key)}/config/discard-draft`,
        {},
      ),

    // Destinations
    listDestinations: () => client.get<Page<Destination>>('/partner/destinations', { query: { limit: 200 } }),
    createDestination: (kind: DestinationKind, config: Record<string, unknown>) =>
      client.post<DestinationCreated>('/partner/destinations', { kind, config }),
    updateDestination: (id: string, patch: { config?: Record<string, unknown>; status?: 'active' | 'disabled' }) =>
      client.patch<Destination>(`/partner/destinations/${enc(id)}`, patch),
    rotateSecret: (id: string) => client.post<{ secret: string }>(`/partner/destinations/${enc(id)}/rotate-secret`, {}),
    testDestination: (id: string) => client.post<{ delivery_id: string | number }>(`/partner/destinations/${enc(id)}/test`, {}),
    listDeliveries: (id: string, status?: DeliveryStatus, cursor?: string) =>
      client.get<Page<Delivery>>(`/partner/destinations/${enc(id)}/deliveries`, { query: { status, cursor } }),

    // Leads
    listLeads: (filters: Record<string, string | undefined>, cursor?: string) =>
      client.get<Page<LeadSummary>>('/partner/leads', { query: { ...filters, cursor } }),
    getLead: (id: string) => client.get<LeadDetail>(`/partner/leads/${enc(id)}`),
    leadTimeline: (id: string) => client.get<{ items: TimelineEntry[] }>(`/partner/leads/${enc(id)}/timeline`),
    resendDelivery: (id: string, deliveryId: number) =>
      client.post<{ delivery_id: number }>(`/partner/leads/${enc(id)}/deliveries/${deliveryId}/resend`, {}),
    reassignLead: (id: string, vendor_id: string, reason?: string) =>
      client.post<Record<string, unknown>>(`/partner/leads/${enc(id)}/reassign`, reason ? { vendor_id, reason } : { vendor_id }),
    closeLead: (id: string, body: { outcome: string; outcome_class?: string; note?: string }) =>
      client.post<LeadSummary>(`/partner/leads/${enc(id)}/close`, body),
    reopenLead: (id: string, note?: string) =>
      client.post<LeadSummary>(`/partner/leads/${enc(id)}/reopen`, note ? { note } : {}),

    // Contractor roster
    listVendors: (q: { trade?: string; active?: 'true' | 'false' | 'all'; q?: string; limit?: number } = {}, cursor?: string) =>
      client.get<Page<Vendor>>('/partner/vendors', { query: { ...q, cursor } }),
    createVendor: (input: VendorInput) => client.post<Vendor>('/partner/vendors', input),
    updateVendor: (id: string, patch: Partial<VendorInput>) => client.patch<Vendor>(`/partner/vendors/${enc(id)}`, patch),
    disableVendor: (id: string) => client.delete<void>(`/partner/vendors/${enc(id)}`),
    // Contractor opt-outs (migration 084)
    recordVendorReconsent: (id: string, input: VendorReconsentInput) =>
      client.post<Vendor>(`/partner/vendors/${enc(id)}/reconsent`, input),
    sendVendorResubscribeLink: (id: string) =>
      client.post<{ sent_to: string | null; expires_at: string }>(`/partner/vendors/${enc(id)}/resubscribe-link`, {}),
    listVendorConsentEvents: (q: { vendor_id?: string; limit?: number } = {}, cursor?: string) =>
      client.get<Page<VendorConsentEvent>>('/partner/vendor-consent-events', { query: { ...q, cursor } }),
    importVendors: (csv: string, dry_run: boolean) => client.post<VendorImportResult>('/partner/vendors/import', { csv, dry_run }),
    bulkSetVendorsActive: (vendor_ids: string[], active: boolean, contactRulesAttested = false) =>
      client.post<VendorBulkResult>('/partner/vendors/bulk', {
        vendor_ids,
        active,
        ...(active && contactRulesAttested ? { contact_rules_attested: true } : {}),
      }),

    // Routing: the accept deadline, the cascade and the rules (package B2)
    getRouting: () => client.get<RoutingResponse>('/partner/routing'),
    updateRouting: (patch: RoutingPatch) => client.patch<RoutingResponse>('/partner/routing', patch),
    replaceRoutingRules: (rules: RoutingRuleInput[]) =>
      client.put<{ rules: RoutingRule[] }>('/partner/routing/rules', { rules }),

    // Dashboard
    funnel: (q: { from?: string; to?: string; origin?: string; trade?: string; site_key?: string }) =>
      client.get<Funnel>('/partner/dashboard/funnel', { query: q }),
    installHealth: (site_key?: string) => client.get<InstallHealth>('/partner/dashboard/install-health', { query: { site_key } }),
    startExport: (from: string, to: string) =>
      client.post<{ job_id: string }>('/partner/dashboard/export', { from, to, format: 'csv' }),
    exportStatus: (jobId: string) => client.get<ExportJob>(`/partner/dashboard/export/${enc(jobId)}`),
    downloadExport: (downloadUrl: string) => client.download(downloadUrl),
    createShareLink: (body: ShareLinkCreateBody = {}) => client.post<ShareLinkCreated>('/partner/dashboard/share-link', body),
    listShareLinks: () => client.get<{ items: ShareLink[] }>('/partner/dashboard/share-links'),
    revokeShareLink: (id: string) => client.delete<void>(`/partner/dashboard/share-link/${enc(id)}`),

    // Hosted pages (finderlyfix.com/p/{slug}), up to `limit` (3) per organisation.
    listHostedPages: () => client.get<HostedPageList>('/partner/hosted-pages'),
    createHostedPage: (body: HostedPageCreateBody = {}) => client.post<HostedPage>('/partner/hosted-pages', body),
    updateHostedPage: (slug: string, patch: HostedPagePatch) =>
      client.patch<HostedPage>(`/partner/hosted-pages/${enc(slug)}`, patch),
    /** Switches the page off and takes it off the list (kept on file; the address stays yours). */
    removeHostedPage: (slug: string) => client.delete<void>(`/partner/hosted-pages/${enc(slug)}`),
    /** Deprecated singular route (the organisation's first page); only the list's fallback for an older API uses it. */
    getHostedPage: () => client.get<HostedPage>('/partner/hosted-page'),

    // Audit
    // Agent access (migration 070)
    listAgentTokens: () => client.get<{ items: AgentToken[] }>('/partner/agent-tokens'),
    createAgentToken: (body: AgentTokenCreateBody) => client.post<AgentTokenCreated>('/partner/agent-tokens', body),
    revokeAgentToken: (id: string) => client.delete<void>(`/partner/agent-tokens/${enc(id)}`),
    // MCP OAuth consent (T097)
    getOAuthRequest: (handle: string) => client.get<OAuthRequestDetails>(`/partner/oauth/requests/${enc(handle)}`),
    approveOAuthRequest: (handle: string, body: OAuthApproveBody) =>
      client.post<{ redirect_to: string; agent_token_id: string }>(`/partner/oauth/requests/${enc(handle)}/approve`, body),
    denyOAuthRequest: (handle: string) => client.post<{ redirect_to: string }>(`/partner/oauth/requests/${enc(handle)}/deny`),
    getAgentSetupBundle: () => client.get<AgentSetupBundle>('/partner/agent-setup-bundle'),
    listPendingChanges: () => client.get<{ items: PendingChange[] }>('/partner/pending-changes'),
    approvePendingChange: (id: string) => client.post<PendingChangeApproved>(`/partner/pending-changes/${enc(id)}/approve`, {}),
    rejectPendingChange: (id: string) => client.post<{ change: PendingChange }>(`/partner/pending-changes/${enc(id)}/reject`, {}),
    // Migration 096: undo an agent's applied change, or keep it (off the dashboard banner).
    undoPendingChange: (id: string) => client.post<{ change: PendingChange }>(`/partner/pending-changes/${enc(id)}/undo`, {}),
    keepPendingChange: (id: string) => client.post<{ change: PendingChange }>(`/partner/pending-changes/${enc(id)}/keep`, {}),
    // The emailed links: no session, the token is the authority.
    previewAgentChangeLink: (token: string) =>
      client.post<AgentChangeLinkPreview>('/partner/agent-change-links/preview', { token }, { auth: false }),
    actAgentChangeLink: (token: string) =>
      client.post<{ action: AgentChangeLinkPreview['action']; change: PendingChange }>('/partner/agent-change-links/act', { token }, { auth: false }),

    audit: (q: { action?: string; actor?: string; agent_token?: string }, cursor?: string) =>
      client.get<Page<AuditEntry>>('/partner/audit', { query: { ...q, cursor } }),

    // FinderlyFix operator console (migration 076). 404 for anyone without an operator grant.
    opsSummary: () => client.get<{ counts: OpsCounts; fallback_requests?: number; limit_requests?: number }>('/partner/ops/summary'),
    opsPartners: (q: { lifecycle?: PartnerLifecycle; q?: string; limit?: number } = {}, cursor?: string) =>
      client.get<Page<OpsPartner>>('/partner/ops/partners', { query: { ...q, cursor } }),
    opsPartner: (id: string) => client.get<OpsPartnerDetail>(`/partner/ops/partners/${enc(id)}`),
    opsApprove: (id: string) => client.post<OpsPartnerState>(`/partner/ops/partners/${enc(id)}/approve`, { confirm: id }),
    opsDisable: (id: string, confirmSlug: string) =>
      client.post<OpsPartnerState>(`/partner/ops/partners/${enc(id)}/disable`, { confirm: id, confirm_slug: confirmSlug }),
    /** Frees the organisation's website so a new sign-up can prove it (disabled/offboarded orgs only). */
    opsReleaseSite: (id: string, confirmSlug: string) =>
      client.post<OpsPartner>(`/partner/ops/partners/${enc(id)}/release-site`, { confirm: id, confirm_slug: confirmSlug }),
    opsSetNetworkFallback: (id: string, siteKey: string, enabled: boolean, note: string) =>
      client.put<OpsNetworkFallback>(`/partner/ops/partners/${enc(id)}/sites/${enc(siteKey)}/network-fallback`, {
        confirm: id,
        enabled,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    opsFallbackRequests: () => client.get<{ items: OpsFallbackRequest[] }>('/partner/ops/network-fallback/requests'),
    opsWidgetLimitRequests: () => client.get<{ items: OpsWidgetLimitRequest[] }>('/partner/ops/widget-limits/requests'),
    opsApproveWidgetLimitRequest: (id: string, grant: WidgetLimitValues, note: string) =>
      client.post<OpsWidgetLimitRequest>(`/partner/ops/widget-limits/requests/${enc(id)}/approve`, {
        ...grant,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    opsDeclineWidgetLimitRequest: (id: string, reason: string) =>
      client.post<OpsWidgetLimitRequest>(`/partner/ops/widget-limits/requests/${enc(id)}/decline`, { reason: reason.trim() }),
    opsApproveFallbackRequest: (id: string, note: string) =>
      client.post<OpsFallbackRequest>(`/partner/ops/network-fallback/requests/${enc(id)}/approve`, note.trim() ? { note: note.trim() } : {}),
    opsDeclineFallbackRequest: (id: string, reason: string) =>
      client.post<OpsFallbackRequest>(`/partner/ops/network-fallback/requests/${enc(id)}/decline`, { reason: reason.trim() }),

    // Public read-only dashboard — no partner auth; the token is the credential.
    getSharedDashboard: (token: string) => client.get<SharedDashboard>(`/share/${enc(token)}`, { auth: false }),

    // Public lead brief — no partner auth; the token is the credential.
    getBrief: (token: string) => client.get<BriefResponse>(`/l/${enc(token)}`, { auth: false }),
    briefAction: (
      token: string,
      action: 'accept' | 'decline' | 'contacted',
      options: { reason?: string; contactRulesVersion?: string } = {},
    ) =>
      client.post<BriefActionResponse>(
        `/l/${enc(token)}/${action}`,
        {
          ...(options.reason ? { reason: options.reason } : {}),
          ...(options.contactRulesVersion ? { contact_rules_version: options.contactRulesVersion } : {}),
        },
        { auth: false },
      ),

    // WS-A2 additions below
    // Hosted page profile (contractor pages phase 1): contact, services, hours, photos. Owner/admin.
    getHostedPageProfile: (slug: string) =>
      client.get<import('./types').HostedPageProfileResponse>(`/partner/hosted-pages/${enc(slug)}/profile`),
    /** Every key optional; an array replaces the stored one. Answers the same shape as the GET. */
    updateHostedPageProfile: (slug: string, body: Partial<import('./types').HostedPageProfileInput>) =>
      client.put<import('./types').HostedPageProfileResponse>(`/partner/hosted-pages/${enc(slug)}/profile`, body),
    /** Multipart `file` + `kind` (+ `alt`, `caption`). 409 GALLERY_FULL past 12 photos, 413 too large, 415 not an image. */
    uploadHostedPageAsset: (slug: string, upload: import('./types').HostedPageAssetUpload) => {
      const form = new FormData();
      form.append('file', upload.file);
      form.append('kind', upload.kind);
      if (upload.alt) form.append('alt', upload.alt);
      if (upload.caption) form.append('caption', upload.caption);
      return client.uploadForm<{ asset: import('./types').HostedPageAsset }>(`/partner/hosted-pages/${enc(slug)}/assets`, form);
    },
    updateHostedPageAsset: (slug: string, id: string, patch: import('./types').HostedPageAssetPatch) =>
      client.patch<{ asset: import('./types').HostedPageAsset }>(`/partner/hosted-pages/${enc(slug)}/assets/${enc(id)}`, patch),
    deleteHostedPageAsset: (slug: string, id: string) => client.delete<void>(`/partner/hosted-pages/${enc(slug)}/assets/${enc(id)}`),
    // WS-B2 additions below
    // Contractor billing (phase 2). Types are imported inline so this region
    // never touches the shared import block above.
    getBilling: () => client.get<import('./types').BillingOverview>('/partner/billing'),
    /** Owner/admin. `version` is the one the Billing screen showed; 409 FEE_SCHEDULE_OUTDATED for any other. */
    acceptFeeSchedule: (version: string) =>
      client.post<import('./types').BillingOverview>('/partner/billing/fee-schedule/accept', { version }),
    updateBillingContact: (patch: import('./types').BillingContactPatch) =>
      client.patch<import('./types').BillingAccountView>('/partner/billing', patch),
    billingLedger: (filters: import('./types').LedgerFilters & { limit?: number } = {}, cursor?: string) =>
      client.get<Page<import('./types').LedgerEntryView>>('/partner/billing/ledger', { query: { ...filters, cursor } }),
    /** The statement as CSV (same filters), fetched with the bearer token like the dashboard export. */
    downloadBillingLedgerCsv: (filters: import('./types').LedgerFilters = {}) => {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(filters)) if (v) params.set(k, String(v));
      const qs = params.toString();
      return client.download(`/partner/billing/ledger.csv${qs ? `?${qs}` : ''}`);
    },
    billingInvoices: () => client.get<{ items: import('./types').InvoiceView[] }>('/partner/billing/invoices'),
    billingInvoice: (id: string) => client.get<import('./types').InvoiceDetail>(`/partner/billing/invoices/${enc(id)}`),
    /** Owner/admin/operator: unlocks the contact and posts the lead fee. 409 FEE_SCHEDULE_REQUIRED / LEAD_NOT_ACCEPTABLE. */
    acceptLead: (id: string) => client.post<LeadDetail>(`/partner/leads/${enc(id)}/accept`, {}),
    // Operator billing (`/partner/ops/...`); every write sends `confirm` = the partner id.
    opsBillingSummary: () => client.get<{ partners: import('./types').OpsBillingSummaryRow[] }>('/partner/ops/billing/summary'),
    opsPartnerBilling: (id: string) => client.get<import('./types').OpsPartnerBilling>(`/partner/ops/partners/${enc(id)}/billing`),
    opsUpdatePartnerBilling: (id: string, update: import('./types').OpsBillingAccountUpdate) =>
      client.put<Record<string, unknown>>(`/partner/ops/partners/${enc(id)}/billing`, { confirm: id, ...update }),
    opsPostBillingEntry: (id: string, input: import('./types').OpsLedgerEntryInput) =>
      client.post<import('./types').LedgerEntryView>(`/partner/ops/partners/${enc(id)}/billing/entries`, { confirm: id, ...input }),
    opsDraftInvoice: (id: string, period_start: string, period_end: string) =>
      client.post<import('./types').InvoiceView>(`/partner/ops/partners/${enc(id)}/billing/invoices`, { confirm: id, period_start, period_end }),
    opsInvoiceAction: (
      id: string,
      invoiceId: string,
      action: import('./types').OpsInvoiceAction,
      extra: { paid_at?: string; external_ref?: string } = {},
    ) =>
      client.post<import('./types').InvoiceView>(`/partner/ops/partners/${enc(id)}/billing/invoices/${enc(invoiceId)}/${action}`, {
        confirm: id,
        ...(extra.paid_at ? { paid_at: extra.paid_at } : {}),
        ...(extra.external_ref?.trim() ? { external_ref: extra.external_ref.trim() } : {}),
      }),
    // WS-C2 additions below
    // Contractor quotes and projects (phase 3). Types are imported inline so
    // this region never touches the shared import block above.
    listLeadQuotes: (leadId: string) => client.get<import('./types').LeadQuotes>(`/partner/leads/${enc(leadId)}/quotes`),
    createQuote: (leadId: string, body: import('./types').QuoteCreateBody) =>
      client.post<import('./types').QuoteCreated>(`/partner/leads/${enc(leadId)}/quotes`, body),
    /** Drafts only; the server refuses a sent quote. */
    updateQuote: (quoteId: string, patch: import('./types').QuotePatch) =>
      client.patch<{ quote: import('./types').LeadQuoteSummary }>(`/partner/quotes/${enc(quoteId)}`, patch),
    sendQuote: (quoteId: string) => client.post<{ quote: import('./types').LeadQuoteSummary }>(`/partner/quotes/${enc(quoteId)}/send`, {}),
    withdrawQuote: (quoteId: string, reason?: string) =>
      client.post<{ quote: import('./types').LeadQuoteSummary }>(`/partner/quotes/${enc(quoteId)}/withdraw`, reason ? { reason } : {}),
    getProject: (projectId: string) => client.get<import('./types').ProjectDetail>(`/partner/projects/${enc(projectId)}`),
    startProject: (projectId: string) => client.post<{ project: import('./types').LeadProjectSummary }>(`/partner/projects/${enc(projectId)}/start`, {}),
    completeProject: (projectId: string, note?: string) =>
      client.post<{ project: import('./types').LeadProjectSummary }>(`/partner/projects/${enc(projectId)}/complete`, note ? { note } : {}),
    cancelProject: (projectId: string, reason: string) =>
      client.post<{ project: import('./types').LeadProjectSummary }>(`/partner/projects/${enc(projectId)}/cancel`, { reason }),
    // The homeowner's quote / completion link — no partner auth; the token is the credential.
    getHomeownerQuote: (token: string) => client.get<import('./types').PublicQuoteResponse>(`/q/${enc(token)}`, { auth: false }),
    acceptHomeownerQuote: (token: string, nameTyped: string) =>
      client.post<import('./types').PublicQuoteActionResponse>(`/q/${enc(token)}/accept`, { confirm: true, name_typed: nameTyped }, { auth: false }),
    declineHomeownerQuote: (token: string, reason?: string) =>
      client.post<import('./types').PublicQuoteActionResponse>(`/q/${enc(token)}/decline`, reason ? { reason } : {}, { auth: false }),
    confirmHomeownerProject: (token: string) =>
      client.post<import('./types').PublicQuoteActionResponse>(`/q/${enc(token)}/confirm`, {}, { auth: false }),
    disputeHomeownerProject: (token: string, reason: string) =>
      client.post<import('./types').PublicQuoteActionResponse>(`/q/${enc(token)}/dispute`, { reason }, { auth: false }),
  };
}

export type PartnerApi = ReturnType<typeof partnerApi>;
